import { useAlert } from "hooks/alerts/useAlert";
import { useCallback } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "services/api.service";
import { Pickup } from "types/pickup";

type PickupPages = {
  pages: Pickup[][];
  pageParams: unknown[];
};

export default function usePickups(skipFiltering: boolean = false, { enabled = true }: { enabled?: boolean } = {}) {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { alertSuccess } = useAlert();
  const listKey = ["pickups", skipFiltering ? "all" : searchParams.toString()] as const;

  const pickupsQuery = useInfiniteQuery({
    queryKey: listKey,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ "page[number]": String(pageParam) });
      if (!skipFiltering) {
        searchParams.forEach((value, key) => {
          params.append(key, value);
        });
      }
      return api.get<{ pickups: Pickup[] }>(`/pickups?${params.toString()}`).then((response) => response.data.pickups);
    },
    enabled,
    getNextPageParam: (lastPage, allPages) => (lastPage.length > 0 ? allPages.length + 1 : undefined),
  });

  const pickups = pickupsQuery.data?.pages.flat() ?? [];

  const getPickupsWithFilters = useCallback((filters: string[]) => {
    const params = new URLSearchParams();
    filters.forEach((filter) => params.append("filter[]", filter));
    return api.get(`/pickups?${params.toString()}`);
  }, []);

  const deletePickup = (pickupUuid: string) =>
    api.delete(`/pickups/${pickupUuid}`).then(() => {
      queryClient.setQueryData<PickupPages>(listKey, (current) => {
        if (!current) return { pages: [], pageParams: [] };
        return {
          ...current,
          pages: current.pages.map((page) => page.filter((pickup) => pickup.uuid !== pickupUuid)),
        };
      });
    });

  const markPickupUnenrolled = (pickupUuid: string) => {
    api.put(`/pickups/${pickupUuid}/mark_unenrolled`).then(() => {
      alertSuccess("Thank you! We will verify your devices as soon as possible.");
      pickupsQuery.refetch();
    });
  };

  return {
    pickups,
    getPickups: () => {
      pickupsQuery.refetch();
    },
    getPickupsWithFilters,
    deletePickup,
    isLoading: pickupsQuery.isLoading || pickupsQuery.isFetchingNextPage,
    errorMessage: pickupsQuery.isError ? "There was a problem loading your pickups." : null,
    hasNextPage: Boolean(pickupsQuery.hasNextPage),
    markPickupUnenrolled,
    incrementCurrentPage: () => {
      pickupsQuery.fetchNextPage();
    },
  };
}
